import { useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import { useRootStore } from "store";
import Notifications from "./components/notifications/Notifications";
import Expandable from "components/expandable/Expandable";
import { pluralizeCustomer } from "common/pluralize/pluralize";
import { clearCompanyName, clearWorkingTabCustomer } from "common/MetaActions";
import Link from "components/link/Link";

import "./Customers.scss";

export default function Customers() {
  const { customerId } = useParams<{ customerId: string }>();

  const location = useLocation();
  const navigate = useNavigate();

  const user = useRootStore((state) => state.user);
  const customers = useRootStore(
    (state) => state.customers.myCustomers.unfilteredCustomers
  );

  const { fetchCustomers } = useRootStore((state) => state.customers);
  const { resetStatus } = useRootStore((state) => state.status);

  const { setWorkingCustomerId, resetCustomer } = useRootStore(
    (state) => state.global
  );
  const { fetchNotifications, dismissNotification } = useRootStore(
    (state) => state.notifications
  );

  const setCustomer = useCallback(() => {
    if (customerId && !["all", "invoices", "my"].includes(customerId)) {
      setWorkingCustomerId(customerId);

      const nextRoute = location.pathname.replace(
        `/customers/${customerId}`,
        ""
      );
      navigate(nextRoute);
    }
  }, [customerId, location.pathname, navigate, setWorkingCustomerId]);

  useEffect(() => {
    setCustomer();
  }, [setCustomer]);

  useEffect(() => {
    resetStatus();
    setWorkingCustomerId("");
    if (user.isAuthenticated) {
      resetCustomer();
      clearWorkingTabCustomer();
      clearCompanyName();
      fetchCustomers();
      setCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuthenticated]);

  const handleFetchNotifications = () => {
    fetchNotifications();
  };

  const handleSetNotificationRead = (id: string) => {
    dismissNotification(id);
  };

  const newCustomers = useMemo(
    () => customers.filter((c) => c.state === "New"),
    [customers]
  );
  const reactivatedCustomers = useMemo(
    () => customers.filter((c) => c.state === "ReactivatedInMaconomy"),
    [customers]
  );

  const countReactivateAndNewCustomers =
    reactivatedCustomers.length + newCustomers.length;

  const customerCount = newCustomers.length + reactivatedCustomers.length;

  const renderNewCustomers = () => {
    if (newCustomers.length === 0) {
      return <></>;
    }

    return (
      <>
        <h3>Nya kunder</h3>
        {newCustomers.map((newCustomer) => {
          const link = "/wizard/" + newCustomer.customerId;
          return (
            <div className="customerNew" key={newCustomer.customerId}>
              <div>
                <strong>{newCustomer.customerName}</strong>
              </div>
              <div>
                <Link to={link}>Sätt upp i MittKontor</Link>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderReactivatedCustomers = () => {
    if (reactivatedCustomers.length === 0) {
      return <></>;
    }

    return (
      <>
        <h3>Återöppnade kunder</h3>
        {reactivatedCustomers.map((reactivatedCustomer) => {
          const link = "/wizard/" + reactivatedCustomer.customerId;
          return (
            <div
              className="customerReactivated"
              key={reactivatedCustomer.customerId}
            >
              <div>
                Återöppnad kund:{" "}
                <strong>{reactivatedCustomer.customerName}</strong>
              </div>
              <div>
                <Link to={link}>Sätt upp återöppnad kund</Link>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Notifications
        fetchNotifications={handleFetchNotifications}
        setNotificationRead={handleSetNotificationRead}
      />

      {countReactivateAndNewCustomers > 0 && (
        <div className="box mb-relative">
          <Expandable
            header={
              <div>
                <h1 className={"m-0"}>
                  Du har {pluralizeCustomer(customerCount)} att sätta upp
                </h1>
              </div>
            }
            expandIconText={"Visa kunder"}
            startMinimized={true}
          >
            {renderNewCustomers()}
            {renderReactivatedCustomers()}
          </Expandable>
        </div>
      )}
      <Outlet />
    </>
  );
}
